import { BaseService } from "@/services/BaseService";
import type { UserFilterOptions } from "@/types/user";
import type {
  AccessibleGuesteriaAdminTenantsResponse,
  CreateGuesteriaAdminDto,
  GuesteriaAdmin,
  GuesteriaAdminListQuery,
  GuesteriaAdminTenantAccessReason,
  GuesteriaAdminsListResponse,
  UpdateGuesteriaAdminDto,
} from "@/types/guesteria-admin";

class GuesteriaAdminsService extends BaseService {
  private readonly basePath = "/admin/guesteria-admins";

  getGuesteriaAdmins(
    query?: GuesteriaAdminListQuery,
  ): Promise<GuesteriaAdminsListResponse> {
    return this.get<GuesteriaAdminsListResponse>(this.basePath, query, {
      skipSelectedHotelHeader: true,
    });
  }

  getFilterOptions(): Promise<UserFilterOptions> {
    return this.get<UserFilterOptions>(
      `${this.basePath}/filter-options`,
      undefined,
      {
        skipSelectedHotelHeader: true,
      },
    );
  }

  getGuesteriaAdminById(id: string): Promise<GuesteriaAdmin> {
    return this.get<GuesteriaAdmin>(`${this.basePath}/${id}`, undefined, {
      skipSelectedHotelHeader: true,
    });
  }

  createGuesteriaAdmin(
    payload: CreateGuesteriaAdminDto,
  ): Promise<GuesteriaAdmin> {
    return this.request<GuesteriaAdmin>(this.basePath, {
      method: "POST",
      body: JSON.stringify(payload),
      skipSelectedHotelHeader: true,
    });
  }

  updateGuesteriaAdmin(
    id: string,
    payload: UpdateGuesteriaAdminDto,
  ): Promise<GuesteriaAdmin> {
    return this.request<GuesteriaAdmin>(`${this.basePath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      skipSelectedHotelHeader: true,
    });
  }

  setGuesteriaAdminActive(
    id: string,
    enabled: boolean,
  ): Promise<GuesteriaAdmin> {
    return this.request<GuesteriaAdmin>(`${this.basePath}/${id}/active`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
      skipSelectedHotelHeader: true,
    });
  }

  deleteGuesteriaAdmin(id: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}`, {
      method: "DELETE",
      skipSelectedHotelHeader: true,
    });
  }

  getAccessibleTenants(
    id: string,
    query?: {
      accessReason?: GuesteriaAdminTenantAccessReason;
      limit?: number;
      offset?: number;
    },
  ): Promise<AccessibleGuesteriaAdminTenantsResponse> {
    return this.get<AccessibleGuesteriaAdminTenantsResponse>(
      `${this.basePath}/${id}/tenants`,
      query,
      {
        skipSelectedHotelHeader: true,
      },
    );
  }
}

export const guesteriaAdminsService = new GuesteriaAdminsService();
